import { MESSAGE_STATUS } from 'shared/constants/messages';

const MESSAGE_STATUS_ORDER = {
  [MESSAGE_STATUS.SENT]: 0,
  [MESSAGE_STATUS.DELIVERED]: 1,
  [MESSAGE_STATUS.READ]: 2,
  [MESSAGE_STATUS.FAILED]: 3,
};

export const findPendingMessageIndex = (chat, message) => {
  const { echo_id: tempMessageId } = message;
  return chat.messages.findIndex(
    m => m.id === message.id || m.id === tempMessageId
  );
};

export const isPendingMessage = ({ id, echo_id: echoId } = {}) =>
  echoId !== undefined && id === echoId;

export const getLatestTimestamp = (...timestamps) => {
  const validTimestamps = timestamps.filter(Number.isFinite);
  return validTimestamps.length ? Math.max(...validTimestamps) : undefined;
};

export const getConversationActivityAt = ({
  last_activity_at: lastActivityAt,
  messages = [],
} = {}) =>
  getLatestTimestamp(
    lastActivityAt,
    ...(messages || []).map(message => message.created_at)
  );

export const shouldKeepExistingMessage = (
  existingMessage = {},
  incomingMessage = {}
) => {
  const currentStatusOrder = MESSAGE_STATUS_ORDER[existingMessage.status];
  const incomingStatusOrder = MESSAGE_STATUS_ORDER[incomingMessage.status];

  if (currentStatusOrder === undefined || incomingStatusOrder === undefined) {
    return false;
  }

  return currentStatusOrder > incomingStatusOrder;
};

export const filterByStatus = (chatStatus, filterStatus) =>
  filterStatus === 'all' ? true : chatStatus === filterStatus;

export const filterByInbox = (shouldFilter, inboxId, chatInboxId) => {
  const isOnInbox = Number(inboxId) === chatInboxId;
  return inboxId ? isOnInbox && shouldFilter : shouldFilter;
};

export const filterByTeam = (shouldFilter, teamId, chatTeamId) => {
  const isOnTeam = (Number(teamId) === chatTeamId || (teamId === 0 && !chatTeamId));
  return teamId != undefined && teamId !== null ? isOnTeam && shouldFilter : shouldFilter;
};

export const filterByLabel = (shouldFilter, labels, chatLabels) => {
  const isOnLabel = labels.every(label => chatLabels.includes(label));
  return labels.length ? isOnLabel && shouldFilter : shouldFilter;
};

export const filterByProductLabel = (shouldFilter, productLabels, chatProductLabels = []) => {
  const isOnProductLabel = productLabels.every(productLabel => chatProductLabels.includes(productLabel));
  return productLabels.length ? isOnProductLabel && shouldFilter : shouldFilter;
};

export const filterByTopicLabel = (shouldFilter, topicLabels, chatTopicLabels = []) => {
  const isOnTopicLabel = topicLabels.every(topicLabel => chatTopicLabels.includes(topicLabel));
  return topicLabels.length ? isOnTopicLabel && shouldFilter : shouldFilter;
};

export const filterByConcurrentLabel = (shouldFilter, concurrentLabels, chatConcurrentLabels = []) => {
  const isOnConcurrentLabel = concurrentLabels.every(concurrentLabel => chatConcurrentLabels.includes(concurrentLabel));
  return concurrentLabels.length ? isOnConcurrentLabel && shouldFilter : shouldFilter;
};

export const filterByUnattended = (
  shouldFilter,
  conversationType,
  assigneeId
) => {
  const isUnassigned = !assigneeId;
  return conversationType === 'unattended'
    ? isUnassigned && shouldFilter
    : shouldFilter;
};

export const filterByLasted = (
  shouldFilter,
  conversationType,
  messages
) => {
  if (conversationType !== 'lasted') return shouldFilter;

  const lastMessage = messages?.[messages.length - 1];
  const isFromContact = lastMessage?.sender_type === 'Contact';

  return isFromContact && shouldFilter;
};

export const filterByUnread = (
  shouldFilter,
  conversationType,
  unreadCount
) => {
  return conversationType === 'unread'
    ? unreadCount > 0 && shouldFilter
    : shouldFilter;
};

export const filterByCampaign = (shouldFilter, campaignFilter, campaignId) => {

  if (!campaignFilter || campaignFilter === 'all') {
    return shouldFilter;
  }

  if (campaignFilter === 'only_campaign') {
    const result =
      campaignId !== null &&
      campaignId !== undefined &&
      shouldFilter;

    return result;
  }

  if (campaignFilter === 'without_campaign') {
    const result =
      campaignId === null ||
      campaignId === undefined;

    return result;
  }

  return shouldFilter;
};

export const applyPageFilters = (conversation, filters) => {
  const {
    inboxId,
    status,
    labels = [],
    product_labels: productLabels = [],
    topic_labels: topicLabels = [],
    concurrent_labels: concurrentLabels = [],
    teamId,
    conversationType,
    campaignFilter,
  } = filters;
  const {
    status: chatStatus,
    inbox_id: chatInboxId,
    labels: chatLabels = [],
    product_labels: chatProductLabels = [],
    topic_labels: chatTopicLabels = [],
    concurrent_labels: chatConcurrentLabels = [],
    meta = {},
    unread_count: unreadCount,
    messages = [],
  } = conversation;
  const team = meta.team || {};
  const { id: chatTeamId } = team;

  let shouldFilter = filterByStatus(chatStatus, status);
  shouldFilter = filterByInbox(shouldFilter, inboxId, chatInboxId);
  shouldFilter = filterByTeam(shouldFilter, teamId, chatTeamId);
  shouldFilter = filterByCampaign(shouldFilter, campaignFilter, conversation?.campaign_id);
  shouldFilter = filterByLabel(shouldFilter, labels, chatLabels);
  shouldFilter = filterByProductLabel(shouldFilter, productLabels, chatProductLabels);
  shouldFilter = filterByTopicLabel(shouldFilter, topicLabels, chatTopicLabels);
  shouldFilter = filterByConcurrentLabel(shouldFilter, concurrentLabels, chatConcurrentLabels);
  shouldFilter = filterByUnattended(
    shouldFilter,
    conversationType,
    meta?.assignee?.id
  );  
  shouldFilter = filterByLasted(
    shouldFilter,
    conversationType,
    messages
  );
  shouldFilter = filterByUnread(
    shouldFilter,
    conversationType,
    unreadCount
  );

  return shouldFilter;
};

export const applyCustomFilters = (conversation, filters = []) => {
  const priorityMap = {
    0: 'low',
    1: 'medium',
    2: 'high',
    3: 'urgent'
  };
  const inversePriorityMap = Object.entries(priorityMap).reduce((acc, [key, value]) => {
    acc[value] = Number(key);
    return acc;
  }, {});

  const normalizePriority = value => {
    if (value == null) return undefined;
    if (typeof value === 'number') return priorityMap[value];
    if (priorityMap[+value]) return priorityMap[+value];
    if (inversePriorityMap[value]) return value;
    return value;
  };

  const evaluateFilter = (filter) => {
    const {
      attribute_key,
      filter_operator = 'equal_to',
      values
    } = filter;

    if (Array.isArray(values) && values.includes('all')) {
      return true;
    }

    let conversationValue;

    switch (attribute_key) {
      case 'priority': {
        const raw = conversation?.priority ?? '';
        conversationValue = normalizePriority(raw);
        break;
      }
      case 'team_id':
      conversationValue = conversation.meta?.team?.id;
      break;
      case 'team_name':
      conversationValue = conversation.meta?.team?.name;
      break;
      case 'assignee_id':
      conversationValue = conversation.meta?.assignee?.id;
      break;
      case 'last_message':
      conversationValue = conversation.messages?.[conversation.messages.length - 1]?.sender_type;
      break;
      case 'status':
      conversationValue = conversation.status;
      break;
      default:
      conversationValue = conversation[attribute_key];
    }

    const isValueMissing =
    conversationValue === undefined ||
    conversationValue === null ||
    (typeof conversationValue === 'string' && conversationValue.trim() === '') ||
    (Array.isArray(conversationValue) && conversationValue.length === 0);

    if (
      isValueMissing &&
      filter_operator !== 'is_present' &&
      filter_operator !== 'is_not_present'
    ) {
      return false;
    }

    const normalizedValues =
    attribute_key === 'priority' ?
    values.map(v => normalizePriority(v)) :
    values;

    let passed = true;

    switch (filter_operator) {
      case 'equal_to':
        if (Array.isArray(conversationValue)) {
          passed = normalizedValues.some(v => conversationValue.includes(v));
        } else {
          passed = normalizedValues.some(v => v == conversationValue);
        }
      break;
      case 'not_equal_to':
      passed = normalizedValues.every(v => v != conversationValue);
      break;
      case 'contains':
      passed = Array.isArray(conversationValue) ?
      normalizedValues.some(v => conversationValue.includes(v)) :
      normalizedValues.some(v => String(conversationValue).includes(v));
      break;
      case 'does_not_contain':
      passed = Array.isArray(conversationValue) ?
      normalizedValues.every(v => !conversationValue.includes(v)) :
      normalizedValues.every(v => !String(conversationValue).includes(v));
      break;
      case 'is_present':
      passed = !isValueMissing;
      break;
      case 'is_not_present':
      passed = isValueMissing;
      break;
      case 'greater_than':
      passed = normalizedValues.some(v => Number(conversationValue) > Number(v));
      break;
      case 'less_than':
      passed = normalizedValues.some(v => Number(conversationValue) < Number(v));
      break;
      default:
      passed = true;
    }

    return passed;
  };

  const groups = [];
  let currentGroup = [];

  filters.forEach((filter, index) => {
    currentGroup.push(filter);
    const nextOperator = filter.query_operator || 'and';
    const isLast = index === filters.length - 1;
    const nextIsOr = nextOperator === 'or';
    if (!nextIsOr || isLast) {
      groups.push([...currentGroup]);
      currentGroup = [];
    }
  });

  const result = groups.every(group => group.some(evaluateFilter));
  return result;
};
